import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Select, Spinner, Textarea } from '../ui'
import { Icon } from '../ui/icons'
import {
  fileSize,
  formatDate,
  formatDateTime,
  PATIENT_DOCUMENT_TYPE_COLORS,
  PATIENT_DOCUMENT_TYPE_LABELS,
} from '../../utils/format'
import { downloadDocument } from '../../services/documents'
import { DocumentViewer, type ViewableDocument } from './DocumentViewer'

export interface PatientDocument extends ViewableDocument {
  type: string
  visibleToPatient: boolean
  createdAt: string
  uploadedBy?: { firstName: string; lastName: string; role: string } | null
}

const TYPES = ['LABORATORY', 'IMAGING', 'REPORT', 'OTHER'] as const

const EMPTY_FORM = { type: 'LABORATORY', title: '', examDate: '', notes: '', visibleToPatient: true }

/**
 * Exámenes y documentos del paciente en el panel (P1). Recepción y médico
 * suben resultados de laboratorio e imágenes; lo que quede publicado aparece
 * en el portal del paciente. El archivo nunca se sirve como estático: se pide
 * autenticado y se muestra desde memoria.
 */
export function PatientDocumentsSection({ patientId, encounterId }: { patientId: string; encounterId?: string }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const canDelete = Boolean(user && ['ADMIN', 'DOCTOR'].includes(user.role))
  const fileInput = useRef<HTMLInputElement>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [file, setFile] = useState<File | null>(null)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')
  const [viewing, setViewing] = useState<PatientDocument | null>(null)

  const documents = useQuery({
    queryKey: ['patient-documents', patientId, encounterId ?? null],
    queryFn: async () =>
      (
        await api.get<PatientDocument[]>('/patient-documents', {
          params: { patientId, ...(encounterId ? { encounterId } : {}) },
        })
      ).data,
  })

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] })
  }

  const upload = useMutation({
    mutationFn: async () => {
      const data = new FormData()
      data.append('patientId', patientId)
      if (encounterId) data.append('encounterId', encounterId)
      data.append('type', form.type)
      data.append('title', form.title)
      if (form.examDate) data.append('examDate', form.examDate)
      if (form.notes) data.append('notes', form.notes)
      data.append('visibleToPatient', String(form.visibleToPatient))
      data.append('file', file!)
      return (await api.post('/patient-documents', data)).data
    },
    onSuccess: () => {
      setForm(EMPTY_FORM)
      setFile(null)
      if (fileInput.current) fileInput.current.value = ''
      setAdding(false)
      setError('')
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const setVisibility = useMutation({
    mutationFn: async ({ id, visibleToPatient }: { id: string; visibleToPatient: boolean }) =>
      (await api.patch(`/patient-documents/${id}`, { visibleToPatient })).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/patient-documents/${id}`)).data,
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const list = documents.data ?? []

  return (
    <Card
      title="Exámenes y documentos"
      actions={
        !adding && (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            <Icon name="upload" className="mr-1.5 inline h-4 w-4" />
            Subir documento
          </Button>
        )
      }
    >
      {adding && (
        <div className="mb-4 space-y-3 rounded-lg border border-brand-200 bg-soft p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tipo">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PATIENT_DOCUMENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Fecha del examen">
              <Input type="date" value={form.examDate} onChange={(e) => setForm({ ...form, examDate: e.target.value })} />
            </Field>
            <Field label="Título">
              <Input
                placeholder="Hemograma completo"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Field>
            <Field label="Archivo" hint="PDF, JPG o PNG. Se valida el contenido, no la extensión.">
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-brand-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-brand-700"
              />
            </Field>
          </div>
          <Field label="Notas para el paciente">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-steel-700">
            <input
              type="checkbox"
              checked={form.visibleToPatient}
              onChange={(e) => setForm({ ...form, visibleToPatient: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Publicar en el portal del paciente
          </label>
          <div className="flex gap-2">
            <Button
              variant="cta"
              disabled={upload.isPending || !file || !form.title.trim()}
              onClick={() => upload.mutate()}
            >
              {upload.isPending ? 'Subiendo…' : 'Subir'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false)
                setError('')
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <ErrorText>{error}</ErrorText>

      {documents.isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <EmptyState message="Sin exámenes ni documentos cargados." />
      ) : (
        <ul className="space-y-2">
          {list.map((doc) => (
            <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 p-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium text-ink">
                  <Icon name={doc.mimeType.startsWith('image/') ? 'imaging' : 'document'} className="h-4 w-4 text-steel-500" />
                  {doc.title}
                  <Badge className={PATIENT_DOCUMENT_TYPE_COLORS[doc.type]}>{PATIENT_DOCUMENT_TYPE_LABELS[doc.type]}</Badge>
                  {!doc.visibleToPatient && <Badge className="bg-gray-200 text-gray-700">No publicado</Badge>}
                </p>
                <p className="text-xs text-gray-500">
                  {doc.examDate ? `Examen del ${formatDate(doc.examDate)} · ` : ''}
                  Subido {formatDateTime(doc.createdAt)}
                  {doc.uploadedBy ? ` por ${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName}` : ''} · {fileSize(doc.sizeBytes)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" onClick={() => setViewing(doc)}>
                  <Icon name="view" className="mr-1 inline h-4 w-4" />
                  Ver
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => downloadDocument('staff', doc.id, doc.fileName).catch((err) => setError(apiErrorMessage(err)))}
                >
                  <Icon name="download" className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  title={doc.visibleToPatient ? 'Quitar del portal del paciente' : 'Publicar en el portal del paciente'}
                  onClick={() => setVisibility.mutate({ id: doc.id, visibleToPatient: !doc.visibleToPatient })}
                >
                  <Icon name={doc.visibleToPatient ? 'personRound' : 'close'} className="h-4 w-4" />
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => {
                      if (confirm(`¿Eliminar «${doc.title}» de la ficha? Queda registrado en la auditoría.`)) remove.mutate(doc.id)
                    }}
                  >
                    <Icon name="trash" className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-xs text-gray-400">
        Cada subida, visualización y descarga queda auditada (Ley 20.584 / 21.719).
      </p>

      {viewing && <DocumentViewer document={viewing} source="staff" onClose={() => setViewing(null)} />}
    </Card>
  )
}
